import React, { useState, useEffect } from 'react';
import Card from '@/components/Card/Card';
import Hero from '@components/Hero/Hero';
import StudentSwitcherModal from '@components/StudentSwitcherModal/StudentSwitcherModal';
import { useToolSettings } from '@context/ToolSettingsContext';
import { useOverviewContext } from '@context/OverviewContext';
import { useSelectedStudent } from '@context/SelectedStudentContext';
import { useLinksContext } from '@context/LinksContext';
import { useSchool } from '@context/SchoolContext';
import { getActiveToolIDs, shouldShowK12Zone, shouldShowLearningCoachCommunity, studentPreviewRole, isTeacherPreview } from '@/utils/toolSettingsUtils';
import { getCommunityUrl, getK12ZoneUrl } from '@/utils/linksUtils';
import { sendUserDetails } from '@/utils/trackHeapUser';
import HelpCard from '@assets/images/contact-help.svg';
import heroStrider from '@assets/images/hero-strider.webp';
import { useShipping } from '@/context/ShippingContext';
import { getMappedStatus, getVisibleShippingItems } from '@/utils/shippingUtils';
import { formatShipDate } from '@/utils/dateUtils';
import LearningMaterialsSection from '@/components/LearningMaterialsSection/LearningMaterialsSection';
import CommunityZoneSection from '@/components/CommunityZoneSection/CommunityZoneSection';
import { HomeCardSkeletons } from '@components/Skeletons/Skeletons';
import { useSkeletonLoader } from '@/hooks/useSkeletonLoader/useSkeletonLoader';
import config from '@/config';
import LearningCenter from '@/components/LearningCenter/LearningCenter';
import { useOktaAuth } from '@okta/okta-react';
import { readTasksSnapshotJson } from '@/utils/tasksSnapshotSession';
import { fetchTasksSnapshotAndPersist } from '@/services/taskCompletionApiService';
import { useFilteredTasklistTasks } from '@/hooks/useFilteredTasklistTasks/useFilteredTasklistTasks';

const Overview = () => {
    const { userRoleVal, students, teacherSchools, loggedInUser = null, loading, error, identityId: lcIdentityId } = useOverviewContext();
    const fetchStudents = async () => students ?? [];
    const { selectedStudent } = useSelectedStudent();
    const isLC = userRoleVal === 'LEARNING_COACH';
    const teacherPreview = isTeacherPreview(userRoleVal);
    const previewRole = studentPreviewRole(userRoleVal);
    const hasMultipleStudents = students && students.length > 1;
    const hasMultipleSchools = teacherSchools.length > 1;

    const greetingName =
      isLC || teacherPreview ? loggedInUser?.fname : selectedStudent?.firstName;

    const [showStudentModal, setShowStudentModal] = useState(false);

    const { data: schoolData, error: schoolError } = useSchool();

    const showSkeleton = useSkeletonLoader({
      schoolData,
      schoolError,
      selectedStudent,
      retryDelay: 3000,
      initialDelay: 2000,
      maxRetries: 3,
    });


    const { toolSettings } = useToolSettings();
    const { k12ZoneLink } = useLinksContext();
    const activeToolIDs = getActiveToolIDs(toolSettings);
    const showLearningCoachCommunity = shouldShowLearningCoachCommunity(previewRole, activeToolIDs);
    const showK12Zone = shouldShowK12Zone(previewRole, activeToolIDs);
    const displayK12ZoneLink = getK12ZoneUrl(previewRole, k12ZoneLink ?? undefined);
    const communityUser =
      userRoleVal === 'TEACHER' && loggedInUser
        ? { ...loggedInUser, role: 'STUDENT' }
        : loggedInUser;
    const communityLink = getCommunityUrl();

    const { visibleHeroTasks, visibleLearningCenterTasks, isResolvingPages } =
      useFilteredTasklistTasks();
    const { authState } = useOktaAuth();
    const { shippingData } = useShipping();

    useEffect(() => {
      if (loading || error || teacherPreview) return;
      const token = authState?.accessToken?.accessToken;
      const id = selectedStudent?.identityId;
      if (!token || !id) return;
      if (readTasksSnapshotJson(id) != null) return;
      void fetchTasksSnapshotAndPersist(token, id).catch(() => {});
    }, [
      loading,
      error,
      teacherPreview,
      authState?.accessToken?.accessToken,
      selectedStudent?.identityId,
    ]);

    useEffect(() => {
      if (selectedStudent?.identityId && selectedStudent?.schoolId) {
        const isPrivateSchool = config.app.privateSchoolIds.includes(selectedStudent.schoolId);

        sendUserDetails({
          id: isLC && lcIdentityId ? lcIdentityId : selectedStudent.identityId,
          studentIdentityId: isLC ? selectedStudent.identityId : undefined,
          grade: selectedStudent.gradeId,
          productType: isPrivateSchool ? 'Private' : 'MPS',
          schoolName: selectedStudent.schoolName,
          schoolId: selectedStudent.schoolId,
          entryDate: selectedStudent.entryDate,
          studentCount: students?.length,
          isLC: isLC
        });
      }
    }, [selectedStudent, userRoleVal, students, isLC, lcIdentityId]);

    if (loading) return <div>Loading learning materials...</div>;
    if (error) return <div>Error loading materials: {error}</div>;

    const totalShowDataLength = 2;
    const visibleItems = getVisibleShippingItems(shippingData);

    const boxes = visibleItems
      .filter((item) => item.itemDescription && item.itemTypeCode)
      .map((item, idx) => ({
        boxNumber: idx + 1,
        totalBoxes: visibleItems.length,
        status: getMappedStatus(item.status),
        shippedDate: formatShipDate(item.shipDate),
        itemSubType: item.itemDescription,
        itemDescription: item.itemDescription,
        itemTypeCode: item.itemTypeCode,
      }))
      .slice(0, totalShowDataLength);

    return (
        <>
          {showSkeleton ? (
            <HomeCardSkeletons />
          ) : (
            <>
              <Hero
                role={isLC ? 'LEARNING_COACH' : 'STUDENT'}
                loggedInUserName={greetingName}
                firstName={selectedStudent?.firstName || ''}
                lastName={selectedStudent?.lastName || ''}
                nickName={selectedStudent?.nickName || ''}
                gradeId={selectedStudent?.gradeId || ''}
                schoolShortName={selectedStudent?.schoolShortName || ''}
                schoolName={selectedStudent?.schoolName || ''}
                schoolId={selectedStudent?.schoolId || ''}
                accountId={selectedStudent?.accountId}
                identityId={selectedStudent?.identityId || ''}
                age={selectedStudent?.age}
                entryDate={selectedStudent?.entryDate || ''}
                schoolLogo=""
                heroImage={heroStrider}
                hasMultipleStudents={isLC && hasMultipleStudents}
                hasMultipleSchools={teacherPreview && hasMultipleSchools}
                onSwitchStudent={() => setShowStudentModal(true)}
                onSwitchSchools={() => setShowStudentModal(true)}
                tasks={visibleHeroTasks}
                showTaskBadge={!teacherPreview}
                isTeacherView={teacherPreview}
              />
              <div className="content-area">
                <LearningCenter
                  tasks={visibleLearningCenterTasks}
                  isLoading={isResolvingPages}
                  showProgressBar={!teacherPreview}
                />
              {shippingData.length > 0 && (
              <LearningMaterialsSection
                eyebrow="ClassRoom Materials"
                header="Classroom Materials Delivered to Your Door!"
                description={
                  <>See what you might need and what you can expect to arrive at your door.</>
                }
                boxes={boxes}
                trackingCtaUrl="/classroom-materials-tracker"
              />
              )}

              <div className="cards-container-community-zone">
                <CommunityZoneSection
                  loggedInUser={communityUser}
                  showK12Zone={showK12Zone}
                  showLearningCoachCommunity={showLearningCoachCommunity}
                  communityLink={communityLink}
                  displayK12ZoneLink={displayK12ZoneLink}
                  disableLinks={teacherPreview}
                />
              </div>

              <div className="help-section">
                <Card
                    theme="primary"
                    orientation="horizontal"
                    header="Looking for help?"
                    description="Call, chat, or read FAQs — we're here to help every step of the way."
                    buttonText="Get In Touch"
                    linkUrl="help-and-support"
                    media={HelpCard}
                />
              </div>
            </div>
            </>
          )}

            {showStudentModal && (
              <StudentSwitcherModal
                fetchStudents={fetchStudents}
                onClose={() => setShowStudentModal(false)}
                onStudentSelect={() => setShowStudentModal(false)}
                variant={userRoleVal === 'TEACHER' ? 'school' : 'student'}
              />
            )}
        </>
    );
};

export default Overview;